// WARNING: This registry is parsed by scripts/lint-ws-registry.sh.
// Do not restructure WS_TAG_REGISTRY or WS_TAG_REGISTRY_IGNORE
// exports without updating the lint script.

import { notificationsApi } from '@/store/api/notificationsApi';
import { qcApi } from '@/store/api/qcApi';
import { monitoringApi } from '@/store/api/monitoringApi';
import { actionsApi } from '@/store/api/actionsApi';
import { jobsApi } from '@/store/api/jobsApi';
import { microagentApi } from '@/store/api/microagentApi';
import type { AppDispatch } from '@/store/store';

/**
 * Maps WebSocket action names to RTK Query API slices and tag types
 * to invalidate when that action is received. Adding a new WS message
 * type requires adding one line here (or to the ignore list).
 *
 * Note: job_completed/job_failed only have the Job tag here. Their
 * worker-conditional invalidation (Connector for ConnectorSyncWorker,
 * Document for DocumentIngestWorker) stays in the useWebSocket.ts switch.
 */
export const WS_TAG_REGISTRY: Record<string, readonly { api: unknown; tags: string[] }[]> = {
  qc_result_updated: [{ api: qcApi, tags: ['QcResult', 'QcSummary'] }],
  alert_fired: [{ api: monitoringApi, tags: ['Trace', 'Stats'] }],
  action_executed: [{ api: actionsApi, tags: ['Execution'] }],
  job_completed: [{ api: jobsApi, tags: ['Job'] }],
  job_failed: [{ api: jobsApi, tags: ['Job'] }],
  microagent_event: [{ api: microagentApi, tags: ['Microagent'] }],
  microagent_paired: [{ api: microagentApi, tags: ['PairingStatus', 'Microagent'] }],
  notification_created: [{ api: notificationsApi, tags: ['Notification', 'NotificationCount'] }],
};

/**
 * Actions that intentionally don't need cache invalidation.
 * Each must have a comment explaining why.
 */
export const WS_TAG_REGISTRY_IGNORE: readonly string[] = [
  'job_progress', // Updates jobs Redux slice directly, not cache tags
  'job_cancel', // Client-to-server only
  'microagent_command', // Client-to-server only
  'microagent_log', // Uses per-ID invalidation in custom switch handler
];

export const WS_FALLBACK_POLL_MS = 15_000;

/**
 * Look up a WS action in the registry and invalidate matching tags.
 * No-op for unknown or ignored actions.
 */
// RTK Query api slices all have util.invalidateTags but with complex generics
// that vary per slice. We store them as `unknown` in the registry and cast
// once here rather than seven times in the object literal.
interface ApiWithInvalidate {
  util: { invalidateTags: (tags: string[]) => unknown };
}

export function invalidateTagsForAction(action: string, dispatch: AppDispatch): void {
  const entries = WS_TAG_REGISTRY[action];
  if (!entries) return;
  for (const { api, tags } of entries) {
    dispatch((api as ApiWithInvalidate).util.invalidateTags(tags) as never);
  }
}

/**
 * Invalidate all tags in the registry. Called on WS reconnect
 * to close the gap from missed messages during disconnect.
 */
export function invalidateAllRegistryTags(dispatch: AppDispatch): void {
  for (const entries of Object.values(WS_TAG_REGISTRY)) {
    for (const { api, tags } of entries) {
      dispatch((api as ApiWithInvalidate).util.invalidateTags(tags) as never);
    }
  }
}
